import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { OrderAdminListItem } from "@/services/admin-order.service";
import { PaginationInfo } from "@/types/api-response";
import { formatDateTime } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import { OrderActionsDropdown } from "./OrderActionsDropdown";
import { OrderStatusBadge } from "./OrderStatusBadge";

// ============================================================================
// Types & Constants
// ============================================================================

type OrderColumnId =
  | "index"
  | "orderNumber"
  | "billCode"
  | "tenant"
  | "user"
  | "type"
  | "status"
  | "items"
  | "tax"
  | "total"
  | "placedAt"
  | "actions";

/**
 * How wide each column is drawn.
 *
 * Fixed widths, not percentages: Tenant stacks a name over a full 36-character GUID and User stacks
 * a name over an e-mail over a phone number, so the excess becomes a horizontal scrollbar instead
 * of squeezing every column into the viewport. A `table-layout: fixed` table is already widened to
 * the sum of these, so no `min-w` is needed — only a scrollport that puts the bar somewhere visible.
 */
const COLUMN_WIDTHS: Record<OrderColumnId, string> = {
  index: "w-16",
  orderNumber: "w-64",
  billCode: "w-40",
  tenant: "w-80",
  user: "w-72",
  type: "w-32",
  status: "w-32",
  items: "w-20",
  tax: "w-36",
  total: "w-36",
  placedAt: "w-40",
  actions: "w-24",
};

interface OrderListTableProps {
  orders: OrderAdminListItem[];
  pagination?: PaginationInfo;
  onViewDetails: (orderId: string) => void;
  onComplete?: (order: OrderAdminListItem) => void;
  onCancel?: (order: OrderAdminListItem) => void;
  showActions?: boolean;
  isLoading?: boolean;
}

function getTypeBadge(type: string) {
  const colors: Record<string, string> = {
    NewService: "bg-ok-soft text-ok-ink",
    Upgrade: "bg-info-soft text-info-ink",
    Renewal: "bg-brand-soft text-brand-ink",
    Addon: "bg-info-soft text-info-ink",
    BalanceTopUp: "bg-warn-soft text-warn-ink",
  };

  return <Badge className={colors[type] || ""}>{type}</Badge>;
}

// ============================================================================
// Component
// ============================================================================

export function OrderListTable({
  orders,
  pagination,
  onViewDetails,
  onComplete,
  onCancel,
  showActions = true,
  isLoading = false,
}: OrderListTableProps) {
  const navigate = useNavigate();

  // Skeleton rows, never the empty state: "No results found" about a query still in flight is a
  // lie, and a table that appears out of nothing reads as a stall then a jolt. min-h-0 flex-1 so
  // the card keeps its height across every branch.
  if (isLoading) {
    return (
      <div className="min-h-0 flex-1 space-y-3 overflow-hidden rounded-md border p-3">
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton key={index} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  const itemsPerPage =
    pagination?.itemsPerPage ??
    (pagination as unknown as { pageSize?: number })?.pageSize ??
    50;
  const currentPage = pagination?.currentPage ?? 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const visibleColumnCount = showActions ? 12 : 11;

  return (
    /*
      `[&>div]:overflow-visible` targets the wrapper `components/ui/table.tsx` puts around every
      table (`<div class="relative w-full overflow-auto">`), and it is what makes the horizontal
      scrollbar reachable. That wrapper is as tall as the whole table, so while IT owned the
      horizontal overflow its scrollbar rendered below the last row — off the bottom of the
      viewport, where nobody could reach it. Switching it off hands both axes to this div, the one
      with a bounded height. It also repairs the sticky header: `position: sticky` resolves against
      the nearest scrollport, and while that was the inner wrapper — a div that never scrolls
      vertically — the header simply scrolled away.
    */
    <div className="scrollbar-thin min-h-0 flex-1 overflow-auto rounded-md border [&>div]:overflow-visible">
      <Table className="table-fixed border-collapse [&_td:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r">
        {/* Generated from one list rather than written out, so a width can never drift from the
            column it belongs to. */}
        <colgroup>
          {(Object.keys(COLUMN_WIDTHS) as OrderColumnId[])
            .filter((id) => id !== "actions" || showActions)
            .map((id) => (
              <col key={id} className={COLUMN_WIDTHS[id]} />
            ))}
        </colgroup>
        <TableHeader className="sticky top-0 z-30 bg-card">
          <TableRow>
            <TableHead className="text-center">STT</TableHead>
            <TableHead>Order Number</TableHead>
            <TableHead>Bill Code</TableHead>
            <TableHead>Tenant</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-center">Items</TableHead>
            <TableHead className="text-right">Tax</TableHead>
            <TableHead className="text-right">Total</TableHead>
            <TableHead className="text-center">Placed At</TableHead>
            {showActions && (
              <TableHead className="sticky right-0 z-20 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
                Actions
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={visibleColumnCount}
                className="h-24 text-center text-muted-foreground"
              >
                No orders found.
              </TableCell>
            </TableRow>
          ) : (
            orders.map((order, index) => (
              <TableRow key={order.id}>
                <TableCell className="text-center text-muted-foreground">
                  {startIndex + index + 1}
                </TableCell>
                <TableCell>
                  <button
                    onClick={() => onViewDetails(order.id)}
                    className="block max-w-full cursor-pointer truncate text-left font-medium text-primary hover:underline"
                    title={order.orderNumber}
                  >
                    {order.orderNumber}
                  </button>
                </TableCell>
                <TableCell>
                  {/* T-PayGate's code for this order's QR bill; "—" when it was paid another way. */}
                  <span className="select-all font-mono text-xs">
                    {order.tPayGateBillCode || "—"}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="min-w-0 space-y-0.5">
                    <button
                      onClick={() =>
                        navigate(`/admin/tenants/${order.tenantId}`)
                      }
                      className="block max-w-full cursor-pointer truncate text-left font-medium text-primary hover:underline"
                      title={order.tenantName}
                    >
                      {order.tenantName}
                    </button>
                    <p
                      className="select-all truncate font-mono text-[11px] text-muted-foreground"
                      title={order.tenantId}
                    >
                      {order.tenantId}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="min-w-0 space-y-0.5 text-sm">
                    {order.userFullName &&
                      order.userFullName.trim().length > 0 && (
                        <div
                          className="truncate font-medium"
                          title={order.userFullName}
                        >
                          {order.userFullName}
                        </div>
                      )}
                    <div
                      className={
                        order.userFullName &&
                        order.userFullName.trim().length > 0
                          ? "truncate text-xs text-muted-foreground"
                          : "truncate font-medium"
                      }
                      title={order.userEmail}
                    >
                      {order.userEmail}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {order.userPhoneNumber ?? "—"}
                    </div>
                  </div>
                </TableCell>
                <TableCell>{getTypeBadge(order.type)}</TableCell>
                <TableCell>
                  <OrderStatusBadge status={order.status} />
                </TableCell>
                <TableCell className="text-center">
                  {order.itemsCount}
                </TableCell>
                <TableCell className="text-right">
                  {/* The amount with the rate under it: the rate is fixed on the order when it is
                      created, so it can differ from row to row once the VAT setting changes. */}
                  <div>{formatCurrency(order.tax, order.currency)}</div>
                  <div className="text-xs text-muted-foreground">
                    {order.taxRate}%
                  </div>
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {formatCurrency(order.total, order.currency)}
                </TableCell>
                <TableCell className="whitespace-nowrap text-center text-sm text-muted-foreground">
                  {formatDateTime(order.placedAt)}
                </TableCell>
                {showActions && (
                  <TableCell className="sticky right-0 z-10 bg-card shadow-[inset_1px_0_0_0_var(--border)]">
                    {/* Centred and pinned, so the column stays reachable once the table is wider
                        than the viewport. Rendered even without the settle handlers: the dropdown
                        still offers "View details", and a column that appears and disappears with
                        the operator's role shifts every width after it. */}
                    <div className="flex items-center justify-center">
                      <OrderActionsDropdown
                        order={order}
                        {...(onComplete
                          ? { onComplete: () => onComplete(order) }
                          : {})}
                        {...(onCancel
                          ? { onCancel: () => onCancel(order) }
                          : {})}
                      />
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
